import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { forkJoin, of, switchMap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import {
  AuditClosureCertificate,
  AuditCorrectiveAction,
  AuditCorrectiveActionLink,
  AuditEvent,
  AuditEvidence,
  AuditFinding,
  AuditTransitionRequest
} from '../audit.models';
import { AuditService } from '../audit.service';
import {
  actionLinkTypeOptions,
  actionPriorityOptions,
  actionTypeOptions,
  auditNavItems,
  auditDaysOverdue,
  escalationLevelOptions,
  escalationStatusOptions,
  effectivenessOptions,
  evidenceTypeOptions,
  formatAuditDate,
  isAuditOverdue,
  statusSeverity
} from '../audit-ui';

type TransitionMode = 'submit' | 'verify' | 'reject' | 'close' | 'reopen';

@Component({
  selector: 'app-audit-action-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    @if (loading) {
      <p-progressBar mode="indeterminate"></p-progressBar>
    }

    <app-page-shell>
      <app-page-hero eyebrow="CAPA Workspace" [title]="action.actionNumber || 'Corrective Action'" [subtitle]="action.description || 'Review, submit, verify, or reopen corrective action work.'">
        <div nav>
          @for (item of visibleNavItems; track item.route) {
            <a pButton [routerLink]="item.route" [label]="item.label" [icon]="item.icon" severity="secondary" [outlined]="true"></a>
          }
        </div>
        <div actions>
          <a pButton routerLink="/audit/actions" icon="pi pi-arrow-left" label="Actions" severity="secondary" [outlined]="true"></a>
          <p-button label="Save" icon="pi pi-save" (onClick)="save()" [loading]="saving" [disabled]="!canManage"></p-button>
        </div>
      </app-page-hero>

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <section class="detail-grid">
        <app-page-data-panel title="Action Details" subtitle="Owner, due date, root cause, and effectiveness fields." [resultText]="action.status || '-'">
          <div class="audit-form-grid">
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-wrench"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="action-detail-type" [options]="actionTypeOptions" [(ngModel)]="action.actionType" optionLabel="label" optionValue="value"></p-select>
                <label for="action-detail-type">Type</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="action-detail-priority" [options]="priorityOptions" [(ngModel)]="action.priority" optionLabel="label" optionValue="value"></p-select>
                <label for="action-detail-priority">Priority</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-user"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-inputNumber inputId="action-detail-owner" [(ngModel)]="action.ownerUserId" [useGrouping]="false"></p-inputNumber>
                <label for="action-detail-owner">Owner User ID</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-calendar-clock"></i></p-inputgroup-addon>
              <p-iftalabel>
                <input pInputText id="action-detail-due-at" type="datetime-local" [(ngModel)]="dueAtInput">
                <label for="action-detail-due-at">Due Date</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-shield"></i></p-inputgroup-addon>
              <p-iftalabel>
                <input pInputText id="action-detail-effectiveness-due" type="datetime-local" [(ngModel)]="effectivenessDueInput">
                <label for="action-detail-effectiveness-due">Effectiveness Check Due</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="action-detail-description" rows="4" [(ngModel)]="action.description"></textarea>
                <label for="action-detail-description">Description</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-stop-circle"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="action-detail-containment" rows="3" [(ngModel)]="action.containmentAction"></textarea>
                <label for="action-detail-containment">Containment Action</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-check-square"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="action-detail-correction" rows="3" [(ngModel)]="action.correctionAction"></textarea>
                <label for="action-detail-correction">Corrective Action</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-shield"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="action-detail-preventive" rows="3" [(ngModel)]="action.preventiveAction"></textarea>
                <label for="action-detail-preventive">Preventive Action</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-refresh"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="action-detail-recurrence" rows="3" [(ngModel)]="action.recurrencePreventionPlan"></textarea>
                <label for="action-detail-recurrence">Recurrence Prevention Plan</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-search"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="action-detail-root-cause" rows="3" [(ngModel)]="action.rootCause"></textarea>
                <label for="action-detail-root-cause">Root Cause</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-shield"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="action-detail-effectiveness" [options]="effectivenessOptions" [(ngModel)]="action.effectivenessResult" optionLabel="label" optionValue="value"></p-select>
                <label for="action-detail-effectiveness">Effectiveness</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="action-detail-effectiveness-notes" rows="3" [(ngModel)]="action.effectivenessNotes"></textarea>
                <label for="action-detail-effectiveness-notes">Effectiveness Notes</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-eye"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="action-detail-evidence-assessment" rows="3" [(ngModel)]="action.evidenceAssessment"></textarea>
                <label for="action-detail-evidence-assessment">Evidence Assessment</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-bell"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="action-detail-escalation-status" [options]="escalationStatusOptions" [(ngModel)]="action.escalationStatus" optionLabel="label" optionValue="value"></p-select>
                <label for="action-detail-escalation-status">Escalation Status</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-angle-double-up"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="action-detail-escalation-level" [options]="escalationLevelOptions" [(ngModel)]="action.escalationLevel" optionLabel="label" optionValue="value"></p-select>
                <label for="action-detail-escalation-level">Escalation Level</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="action-detail-escalation-notes" rows="3" [(ngModel)]="action.escalationNotes"></textarea>
                <label for="action-detail-escalation-notes">Escalation Notes</label>
              </p-iftalabel>
            </p-inputgroup>
          </div>
        </app-page-data-panel>

        <app-page-data-panel title="Finding Context" subtitle="Corrective action source finding." [resultText]="finding.findingNumber || '-'">
          <div class="audit-context">
            <p-tag [value]="finding.severity || '-'" [severity]="statusSeverity(finding.severity)"></p-tag>
            <strong>{{ finding.title || '-' }}</strong>
            <span>{{ finding.description || 'No finding description available.' }}</span>
            <a pButton [routerLink]="['/audit/findings', finding.findingId]" label="Open Finding" icon="pi pi-arrow-right" iconPos="right" [outlined]="true" severity="secondary"></a>
          </div>
        </app-page-data-panel>
      </section>

      <section class="action-strip">
        <p-button label="Submit" icon="pi pi-send" (onClick)="openTransition('submit')" [disabled]="!canManage || !canSubmit"></p-button>
        <p-button label="Verify" icon="pi pi-check-circle" severity="success" (onClick)="openTransition('verify')" [disabled]="!canVerify || action.status !== 'SUBMITTED'"></p-button>
        <p-button label="Reject" icon="pi pi-times-circle" severity="danger" [outlined]="true" (onClick)="openTransition('reject')" [disabled]="!canVerify || action.status !== 'SUBMITTED'"></p-button>
        <p-button label="Close" icon="pi pi-lock" severity="secondary" (onClick)="openTransition('close')" [disabled]="!canManage || action.status !== 'VERIFIED'"></p-button>
        <p-button label="Escalate" icon="pi pi-bell" severity="warn" [outlined]="true" (onClick)="escalate()" [disabled]="!canManage || action.status === 'CLOSED'"></p-button>
        <p-button label="Reopen" icon="pi pi-refresh" severity="warn" [outlined]="true" (onClick)="openTransition('reopen')" [disabled]="!canManage || action.status === 'OPEN'"></p-button>
      </section>

      <section class="detail-grid">
        <app-page-data-panel title="Action Evidence" subtitle="Metadata links for action completion evidence." [resultText]="evidence.length + ' records'">
          <div class="panel-actions">
            <p-button label="Add Evidence" icon="pi pi-plus" (onClick)="openEvidence()" [disabled]="!canUploadEvidence"></p-button>
          </div>
          <p-table [value]="evidence" styleClass="p-datatable-sm" responsiveLayout="scroll">
            <ng-template pTemplate="header">
              <tr><th>Evidence</th><th>Type</th><th>Captured</th><th>Hash</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td><a [href]="item.fileUrl" target="_blank" rel="noopener" [class.disabled-link]="!item.fileUrl">{{ item.fileName || item.sourceDisplayName || '-' }}</a></td>
                <td>{{ item.evidenceType || '-' }}</td>
                <td>{{ formatDate(item.capturedAt) }}</td>
                <td>{{ item.sha256Hash || '-' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="4" class="text-center p-4">No action evidence metadata has been added.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>

        <app-page-data-panel title="Linked Training / Process / System Changes" subtitle="References that prevent recurrence." [resultText]="links.length + ' links'">
          <div class="panel-actions">
            <p-button label="Link Reference" icon="pi pi-link" (onClick)="openLink()" [disabled]="!canManage"></p-button>
          </div>
          <p-table [value]="links" styleClass="p-datatable-sm" responsiveLayout="scroll">
            <ng-template pTemplate="header">
              <tr><th>Reference</th><th>Type</th><th>Resource</th><th>Reason</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-link>
              <tr>
                <td>
                  @if (link.referenceUrl) {
                    <a [href]="link.referenceUrl" target="_blank" rel="noopener">{{ link.referenceTitle || '-' }}</a>
                  } @else {
                    <span>{{ link.referenceTitle || '-' }}</span>
                  }
                </td>
                <td>{{ link.linkType || '-' }}</td>
                <td>{{ link.referenceModule || '-' }} / {{ link.referenceResourceNumber || link.referenceResourceId || '-' }}</td>
                <td>{{ link.reason || '-' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="4" class="text-center p-4">No linked CAPA references have been added.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>

        <app-page-data-panel title="Closure Certificate" subtitle="Generated when a verified action is closed." [resultText]="certificate.closureCertificateNumber || 'Not issued'">
          <div class="audit-context">
            <p-tag [value]="certificate.closureCertificateNumber || 'Pending'" [severity]="certificate.closureCertificateNumber ? 'success' : 'secondary'"></p-tag>
            <strong>{{ certificate.actionNumber || action.actionNumber || '-' }}</strong>
            <span>{{ certificate.closureSummary || action.closureSummary || 'Certificate is issued after closure.' }}</span>
            <span>Certified by: {{ certificate.closureCertifiedBy || '-' }} at {{ formatDate(certificate.closureCertifiedAt) }}</span>
          </div>
        </app-page-data-panel>

        <app-page-data-panel title="Timeline" subtitle="Audit events for the parent case." [resultText]="events.length + ' events'">
          <p-table [value]="events" styleClass="p-datatable-sm" responsiveLayout="scroll">
            <ng-template pTemplate="header">
              <tr><th>Event</th><th>Status</th><th>When</th><th>Comment</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-event>
              <tr>
                <td>{{ event.eventType }}</td>
                <td>{{ event.fromStatus || '-' }} -> {{ event.toStatus || '-' }}</td>
                <td>{{ formatDate(event.performedAt) }}</td>
                <td>{{ event.comment || '-' }}</td>
              </tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>
      </section>
    </app-page-shell>

    <p-dialog [header]="transitionTitle" [(visible)]="transitionVisible" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(620px, 96vw)' }">
      <div class="audit-form-grid">
        @if (transitionMode === 'verify') {
          <p-inputgroup class="wide-field">
            <p-inputgroup-addon><i class="pi pi-check-circle"></i></p-inputgroup-addon>
            <p-iftalabel>
              <textarea pTextarea id="action-transition-verification" rows="3" [(ngModel)]="transition.verificationResult"></textarea>
              <label for="action-transition-verification">Verification Result</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-shield"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="action-transition-effectiveness" [options]="effectivenessOptions" [(ngModel)]="transition.effectivenessResult" optionLabel="label" optionValue="value"></p-select>
              <label for="action-transition-effectiveness">Effectiveness</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup class="wide-field">
            <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
            <p-iftalabel>
              <textarea pTextarea id="action-transition-effectiveness-notes" rows="3" [(ngModel)]="transition.effectivenessNotes"></textarea>
              <label for="action-transition-effectiveness-notes">Effectiveness Notes</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup class="wide-field">
            <p-inputgroup-addon><i class="pi pi-eye"></i></p-inputgroup-addon>
            <p-iftalabel>
              <textarea pTextarea id="action-transition-evidence-assessment" rows="3" [(ngModel)]="transition.evidenceAssessment"></textarea>
              <label for="action-transition-evidence-assessment">Evidence Assessment</label>
            </p-iftalabel>
          </p-inputgroup>
        } @else {
          <p-inputgroup class="wide-field">
            <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
            <p-iftalabel>
              <textarea pTextarea id="action-transition-comment" rows="4" [(ngModel)]="transition.comment"></textarea>
              <label for="action-transition-comment">{{ transitionMode === 'reject' || transitionMode === 'reopen' ? 'Reason' : 'Comment' }}</label>
            </p-iftalabel>
          </p-inputgroup>
          @if (transitionMode === 'reject' || transitionMode === 'reopen') {
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-eye-slash"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="action-transition-weak-evidence" rows="3" [(ngModel)]="transition.weakEvidenceReason"></textarea>
                <label for="action-transition-weak-evidence">Weak Evidence Reason</label>
              </p-iftalabel>
            </p-inputgroup>
          }
          @if (transitionMode === 'close') {
            <p-inputgroup class="wide-field">
              <p-inputgroup-addon><i class="pi pi-certificate"></i></p-inputgroup-addon>
              <p-iftalabel>
                <textarea pTextarea id="action-transition-closure-summary" rows="3" [(ngModel)]="transition.closureSummary"></textarea>
                <label for="action-transition-closure-summary">Closure Certificate Summary</label>
              </p-iftalabel>
            </p-inputgroup>
          }
        }
      </div>
      @if (dialogError) {
        <div class="audit-error">{{ dialogError }}</div>
      }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="transitionVisible = false"></p-button>
        <p-button label="Confirm" icon="pi pi-check" (onClick)="runTransition()" [loading]="saving"></p-button>
      </ng-template>
    </p-dialog>

    <p-dialog header="Add Action Evidence" [(visible)]="evidenceVisible" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(720px, 96vw)' }">
      <div class="audit-form-grid">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-paperclip"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="action-evidence-type" [options]="evidenceTypeOptions" [(ngModel)]="evidenceForm.evidenceType" optionLabel="label" optionValue="value"></p-select>
            <label for="action-evidence-type">Evidence Type</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-file"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="action-evidence-file-name" [(ngModel)]="evidenceForm.fileName">
            <label for="action-evidence-file-name">File Name</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-link"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="action-evidence-file-url" [(ngModel)]="evidenceForm.fileUrl">
            <label for="action-evidence-file-url">File URL</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-code"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="action-evidence-content-type" [(ngModel)]="evidenceForm.contentType">
            <label for="action-evidence-content-type">Content Type</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="action-evidence-sha" [(ngModel)]="evidenceForm.sha256Hash">
            <label for="action-evidence-sha">SHA-256</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-database"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="action-evidence-metadata" rows="3" [(ngModel)]="evidenceForm.metadataJson"></textarea>
            <label for="action-evidence-metadata">Metadata JSON</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>
      @if (dialogError) {
        <div class="audit-error">{{ dialogError }}</div>
      }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="evidenceVisible = false"></p-button>
        <p-button label="Add Evidence" icon="pi pi-check" (onClick)="saveEvidence()" [loading]="saving" [disabled]="!canUploadEvidence"></p-button>
      </ng-template>
    </p-dialog>

    <p-dialog header="Link CAPA Reference" [(visible)]="linkVisible" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(760px, 96vw)' }">
      <div class="audit-form-grid">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-link"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="action-link-type" [options]="actionLinkTypeOptions" [(ngModel)]="linkForm.linkType" optionLabel="label" optionValue="value"></p-select>
            <label for="action-link-type">Link Type</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-sitemap"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="action-link-module" [(ngModel)]="linkForm.referenceModule">
            <label for="action-link-module">Reference Module</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-bookmark"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="action-link-title" [(ngModel)]="linkForm.referenceTitle">
            <label for="action-link-title">Reference Title</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-database"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="action-link-resource-type" [(ngModel)]="linkForm.referenceResourceType">
            <label for="action-link-resource-type">Resource Type</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="action-link-resource-number" [(ngModel)]="linkForm.referenceResourceNumber">
            <label for="action-link-resource-number">Resource Number</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-globe"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="action-link-url" [(ngModel)]="linkForm.referenceUrl">
            <label for="action-link-url">Reference URL</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="action-link-reason" rows="3" [(ngModel)]="linkForm.reason"></textarea>
            <label for="action-link-reason">Reason</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>
      @if (dialogError) {
        <div class="audit-error">{{ dialogError }}</div>
      }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="linkVisible = false"></p-button>
        <p-button label="Link Reference" icon="pi pi-check" (onClick)="saveLink()" [loading]="saving" [disabled]="!canManage"></p-button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .detail-grid {
      display: grid;
      grid-template-columns: minmax(0, 1.4fr) minmax(280px, .8fr);
      gap: 1rem;
      margin-bottom: 1rem;
    }

    .audit-form-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(180px, 1fr));
      gap: .85rem;
    }

    label,
    .audit-context {
      color: #334155;
      display: grid;
      gap: .35rem;
      min-width: 0;
    }

    label span {
      color: #64748b;
      font-size: .82rem;
      font-weight: 700;
    }

    input,
    textarea,
    p-iftalabel,
    p-inputgroup,
    p-select,
    p-inputNumber {
      width: 100%;
    }

    .wide-field {
      grid-column: 1 / -1;
    }

    .action-strip,
    .panel-actions {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: .75rem;
      justify-content: flex-end;
      margin-bottom: 1rem;
    }

    .audit-error {
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      color: #991b1b;
      font-weight: 700;
      margin-top: .85rem;
      padding: .75rem;
    }

    .disabled-link {
      color: #64748b;
      pointer-events: none;
      text-decoration: none;
    }

    @media (max-width: 980px) {
      .detail-grid,
      .audit-form-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class AuditActionDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly auditService = inject(AuditService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  saving = false;
  transitionVisible = false;
  evidenceVisible = false;
  linkVisible = false;
  dialogError = '';
  transitionMode: TransitionMode = 'submit';
  action: AuditCorrectiveAction = {};
  finding: AuditFinding = {};
  evidence: AuditEvidence[] = [];
  links: AuditCorrectiveActionLink[] = [];
  certificate: AuditClosureCertificate = {};
  events: AuditEvent[] = [];
  transition: AuditTransitionRequest = {};
  evidenceForm: AuditEvidence = {};
  linkForm: AuditCorrectiveActionLink = {};
  dueAtInput = '';
  effectivenessDueInput = '';

  readonly visibleNavItems = auditNavItems.filter(item => this.permissionService.hasPermission(item.permission));
  readonly canManage = this.permissionService.hasPermission(app_permissions.audit.actions.manage);
  readonly canVerify = this.permissionService.hasPermission(app_permissions.audit.actions.verify);
  readonly canUploadEvidence = this.permissionService.hasPermission(app_permissions.audit.evidence.upload);
  readonly actionTypeOptions = actionTypeOptions;
  readonly actionLinkTypeOptions = actionLinkTypeOptions;
  readonly priorityOptions = actionPriorityOptions;
  readonly escalationLevelOptions = escalationLevelOptions;
  readonly escalationStatusOptions = escalationStatusOptions;
  readonly effectivenessOptions = effectivenessOptions;
  readonly evidenceTypeOptions = evidenceTypeOptions;
  readonly statusSeverity = statusSeverity;
  readonly formatDate = formatAuditDate;

  ngOnInit(): void {
    this.load();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Status', value: this.action.status || '-' },
      { label: 'Priority', value: this.action.priority || '-' },
      { label: 'Evidence', value: this.evidence.length, tone: 'info' },
      { label: 'Linked Changes', value: this.links.length, tone: 'info' },
      { label: 'Weak Evidence', value: this.action.evidenceWeak ? 'Yes' : 'No', tone: this.action.evidenceWeak ? 'danger' : 'success' },
      { label: 'Certificate', value: this.action.closureCertificateNumber || '-', tone: this.action.closureCertificateNumber ? 'success' : 'default' },
      { label: 'Overdue', value: this.isOverdue ? `${this.daysOverdue} days` : 'No', tone: this.isOverdue ? 'danger' : 'success' }
    ];
  }

  get isOverdue(): boolean {
    return Boolean(this.action.overdue) || isAuditOverdue(this.action.dueAt, this.action.status);
  }

  get daysOverdue(): number {
    return this.action.daysOverdue || auditDaysOverdue(this.action.dueAt, this.action.status);
  }

  get canSubmit(): boolean {
    return ['OPEN', 'IN_PROGRESS', 'REJECTED'].includes((this.action.status || '').toUpperCase());
  }

  get transitionTitle(): string {
    const labels: Record<TransitionMode, string> = {
      submit: 'Submit Corrective Action',
      verify: 'Verify Corrective Action',
      reject: 'Reject Corrective Action',
      close: 'Close Corrective Action',
      reopen: 'Reopen Corrective Action'
    };
    return labels[this.transitionMode];
  }

  load(): void {
    const actionId = Number(this.route.snapshot.paramMap.get('id'));
    this.loading = true;
    this.auditService.getAction(actionId).pipe(
      switchMap(actionResponse => {
        this.action = actionResponse.data ?? {};
        this.dueAtInput = this.toLocalInput(this.action.dueAt);
        this.effectivenessDueInput = this.toLocalInput(this.action.effectivenessCheckDueAt);
        return forkJoin({
          finding: this.action.findingId ? this.auditService.getFinding(this.action.findingId) : of({ data: {} as AuditFinding }),
          evidence: this.auditService.evidence(undefined, undefined, undefined, actionId),
          links: this.auditService.actionLinks(actionId),
          certificate: this.auditService.actionClosureCertificate(actionId),
          events: this.action.auditCaseId ? this.auditService.caseEvents(this.action.auditCaseId) : of({ data: [] as AuditEvent[] })
        });
      })
    ).subscribe({
      next: response => {
        this.finding = response.finding.data ?? {};
        this.evidence = response.evidence.data ?? [];
        this.links = response.links.data ?? [];
        this.certificate = response.certificate.data ?? {};
        this.events = response.events.data ?? [];
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Corrective action', detail: 'Unable to load corrective action.' }),
      complete: () => this.loading = false
    });
  }

  save(): void {
    this.action.dueAt = this.dueAtInput ? new Date(this.dueAtInput).toISOString() : undefined;
    this.action.effectivenessCheckDueAt = this.effectivenessDueInput ? new Date(this.effectivenessDueInput).toISOString() : undefined;
    this.saving = true;
    this.auditService.updateAction(this.action).subscribe({
      next: response => {
        this.action = response.data ?? this.action;
        this.messageService.add({ severity: 'success', summary: 'Action saved', detail: response.message || 'Corrective action saved.' });
        this.load();
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Action', detail: error?.error?.message || 'Unable to save corrective action.' }),
      complete: () => this.saving = false
    });
  }

  openTransition(mode: TransitionMode): void {
    this.transitionMode = mode;
    this.transition = {};
    this.dialogError = '';
    this.transitionVisible = true;
  }

  runTransition(): void {
    if ((this.transitionMode === 'reject' || this.transitionMode === 'reopen') && !this.transition.comment?.trim()) {
      this.dialogError = 'A reason is required.';
      return;
    }
    if (this.transition.comment && !this.transition.reason) {
      this.transition.reason = this.transition.comment;
    }
    const actionId = this.action.actionId!;
    const request$ =
      this.transitionMode === 'submit' ? this.auditService.submitAction(actionId, this.transition) :
      this.transitionMode === 'verify' ? this.auditService.verifyAction(actionId, this.transition) :
      this.transitionMode === 'reject' ? this.auditService.rejectAction(actionId, this.transition) :
      this.transitionMode === 'close' ? this.auditService.closeAction(actionId, this.transition) :
      this.auditService.reopenAction(actionId, this.transition);
    this.saving = true;
    request$.subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Action updated', detail: response.message || 'Corrective action updated.' });
        this.transitionVisible = false;
        this.load();
      },
      error: error => this.dialogError = error?.error?.message || 'Unable to update corrective action.',
      complete: () => this.saving = false
    });
  }

  escalate(): void {
    this.dialogError = '';
    const reason = this.action.escalationNotes?.trim() || 'Manual CAPA escalation from action workspace.';
    this.saving = true;
    this.auditService.escalateAction(this.action.actionId!, { reason, comment: reason }).subscribe({
      next: response => {
        this.action = response.data ?? this.action;
        this.messageService.add({ severity: 'success', summary: 'Action escalated', detail: response.message || 'CAPA action escalated.' });
        this.load();
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Escalation', detail: error?.error?.message || 'Unable to escalate action.' }),
      complete: () => this.saving = false
    });
  }

  openLink(): void {
    this.dialogError = '';
    this.linkForm = { actionId: this.action.actionId, linkType: 'PROCESS_CHANGE' };
    this.linkVisible = true;
  }

  saveLink(): void {
    this.dialogError = '';
    if (!this.linkForm.referenceTitle?.trim()) {
      this.dialogError = 'Reference title is required.';
      return;
    }
    this.saving = true;
    this.auditService.linkAction(this.action.actionId!, this.linkForm).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Reference linked', detail: response.message || 'CAPA reference linked.' });
        this.linkVisible = false;
        this.load();
      },
      error: error => this.dialogError = error?.error?.message || 'Unable to link CAPA reference.',
      complete: () => this.saving = false
    });
  }

  openEvidence(): void {
    this.dialogError = '';
    this.evidenceForm = { evidenceType: 'DOCUMENT', actionId: this.action.actionId, findingId: this.action.findingId, auditCaseId: this.action.auditCaseId };
    this.evidenceVisible = true;
  }

  saveEvidence(): void {
    this.dialogError = '';
    if (!this.evidenceForm.evidenceType) {
      this.dialogError = 'Evidence type is required.';
      return;
    }
    this.saving = true;
    this.auditService.createEvidence(this.evidenceForm).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Evidence added', detail: response.message || 'Action evidence added.' });
        this.evidenceVisible = false;
        this.load();
      },
      error: error => this.dialogError = error?.error?.message || 'Unable to add evidence.',
      complete: () => this.saving = false
    });
  }

  private toLocalInput(value?: string | Date): string {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  }
}
